import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import StoreIcon from '@mui/icons-material/Store';
import SearchIcon from '@mui/icons-material/Search';
import { useStores } from '../../hooks/useStores';

export const StoresPage: React.FC = () => {
  const { data: stores, isLoading, error } = useStores();
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredStores = stores?.filter((store) =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.wilaya?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" gutterBottom fontWeight="bold">
        Browse Stores
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Discover stores near you or online
      </Typography>

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search stores by name or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load stores. Please try again later.
        </Alert>
      )}

      {!isLoading && filteredStores?.length === 0 && (
        <Alert severity="info">
          No stores found matching your search criteria.
        </Alert>
      )}

      <Grid container spacing={3}>
        {filteredStores?.map((store) => (
          <Grid item xs={12} sm={6} md={4} key={store.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardMedia
                component="div"
                sx={{
                  height: 160,
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StoreIcon sx={{ fontSize: 70, color: 'white' }} />
              </CardMedia>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h5" gutterBottom>
                  {store.name}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    minHeight: 40,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {store.description || 'No description available'}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={store.type}
                    size="small"
                    color="primary"
                    sx={{ mr: 1 }}
                  />
                  {store.isActive && (
                    <Chip label="Open" size="small" color="success" />
                  )}
                </Box>

                {store.city && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    📍 {store.city}, {store.wilaya || store.country}
                  </Typography>
                )}

                {store.phone && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    📞 {store.phone}
                  </Typography>
                )}

                {store.email && (
                  <Typography variant="body2" color="text.secondary">
                    ✉️ {store.email}
                  </Typography>
                )}
              </CardContent>
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  component={RouterLink}
                  to={`/stores/${store.id}`}
                >
                  View Store
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};
